'use client'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { commercialQuoteHref, filmQuoteHref } from '@/lib/commercial/routes'
import { BUSINESS } from '@/lib/constants'
import BookingTrigger from '@/components/BookingTrigger'
import { TrackedLink } from '@/components/analytics/TrackedLink'

function IconArrowRight(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" {...p}>
      <path d="M5 12 H19 M13 6 L19 12 L13 18" />
    </svg>
  )
}

/* ---------- Inline SVG icons (match shell.jsx exactly) ---------- */
function IconMaple(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M12 2 L13 6 L16 4 L15 8 L19 7 L17 10 L21 11 L17 13 L20 16 L16 16 L17 20 L13 17 L12 22 L11 17 L7 20 L8 16 L4 16 L7 13 L3 11 L7 10 L5 7 L9 8 L8 4 L11 6 Z" />
    </svg>
  )
}
function IconBadge(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" {...p}>
      <path d="M12 2 L15 5 H19 V9 L22 12 L19 15 V19 H15 L12 22 L9 19 H5 V15 L2 12 L5 9 V5 H9 Z" />
      <path d="M8 12 L11 15 L16 10" />
    </svg>
  )
}
function IconInstagram(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

/* ---------- Footer link columns (from shell.jsx) ---------- */
type FooterItem = [label: string, href: string | null]
interface FooterCol {
  h: string
  items: FooterItem[]
}

const FOOTER_COLS: FooterCol[] = [
  {
    h: 'Services',
    items: [
      ['Security Window Film', '/products/window-film'],
      ['Door Fortification', '/products/door-fortification'],
      ['Solar Window Film', '/products/solar-film'],
      ['Privacy Window Film', '/products/privacy-film'],
      ['Commercial Window Film', '/commercial'],
    ],
  },
  {
    h: 'Areas',
    items: [
      ['Toronto', '/service-areas/toronto'],
      ['Mississauga', '/service-areas/mississauga'],
      ['Markham', '/service-areas/markham'],
      ['Oakville', '/service-areas/oakville'],
      ['Thornhill', '/service-areas/thornhill'],
      ['All areas →', '/service-areas'],
    ],
  },
  {
    h: 'Company',
    items: [
      ['About', '/about'],
      ['Contact', '/contact'],
      ['Learning Hub', '/blog'],
      ['Film Comparisons', '/window-film-comparisons'],
      ['Local Watch', '/local-watch'],
      ['FAQ', '/faq'],
      ['Get a Quote', '/quote'],
      ['Press', '/press'],
    ],
  },
  {
    h: 'Contact',
    items: [
      [BUSINESS.phone, `tel:${BUSINESS.phone.replace(/\D/g, '')}`],
      [BUSINESS.email, `mailto:${BUSINESS.email}`],
      ['@ClearGuardGTA', 'https://instagram.com/ClearGuardGTA'],
      // Matches `BUSINESS.hours` ('Mo-Su 09:00-19:00') and /contact. Keep the
      // three in sync, and in sync with the Google Business Profile.
      ['9am–7pm Mon–Sun', null],
    ],
  },
]

export default function Footer() {
  const pathname = usePathname()
  const productQuoteHref = filmQuoteHref(pathname)
  const quoteHref = productQuoteHref ?? commercialQuoteHref(pathname)
  return (
    <footer style={{ background: 'var(--navy-950)', color: 'rgba(255,255,255,0.7)', paddingTop: 80, paddingBottom: 24 }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 32px' }}>
        <div
          className="footer-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
            gap: 48,
            paddingBottom: 64,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {/* Brand column */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Image
                src="/assets/logo-shield.png"
                alt=""
                width={40}
                height={40}
                style={{ height: 40, width: 'auto', filter: 'brightness(1.1)' }}
              />
              <span className="display" style={{ color: '#fff', fontSize: 20, letterSpacing: '0.06em' }}>
                CLEAR GUARD
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20, alignItems: 'flex-start' }}>
              <span className="chip chip-dark" style={{ width: '100%', maxWidth: 320, justifyContent: 'flex-start' }}>
                <IconMaple width={14} height={14} style={{ color: 'var(--maple)' }} /> Canadian Owned &amp; Operated
              </span>
              <span className="chip chip-dark" style={{ width: '100%', maxWidth: 320, justifyContent: 'flex-start' }}>
                <IconBadge width={10} height={10} /> XPEL Certified Installer
              </span>
            </div>
            <address
              className="footer-address"
              style={{ marginTop: 20, fontStyle: 'normal', fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,0.55)' }}
            >
              <span itemProp="streetAddress">{BUSINESS.address.streetAddress}</span>
              <br />
              <span itemProp="addressLocality">{BUSINESS.address.addressLocality}</span>,{' '}
              <span itemProp="addressRegion">{BUSINESS.address.addressRegion}</span>{' '}
              <span itemProp="postalCode">{BUSINESS.address.postalCode}</span>
              <br />
              <TrackedLink
                href={`tel:${BUSINESS.phone.replace(/\D/g, '')}`}
                location="footer"
                label="footer_phone"
                style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}
                itemProp="telephone"
              >
                {BUSINESS.phone}
              </TrackedLink>
              <br />
              <TrackedLink
                href={`mailto:${BUSINESS.email}`}
                location="footer"
                label="footer_email"
                style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}
                itemProp="email"
              >
                {BUSINESS.email}
              </TrackedLink>
            </address>
          </div>

          {/* Link columns */}
          {FOOTER_COLS.map((col) => (
            <div key={col.h}>
              <div className="eyebrow" style={{ color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>
                {col.h}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {col.items.map(([label, href]) => {
                  const isInstagram = href?.includes('instagram.com')
                  const content = isInstagram ? (
                    <IconInstagram width={20} height={20} aria-label="Instagram" />
                  ) : (
                    label
                  )
                  return (
                    <li key={label} style={{ padding: '6px 0', fontSize: 13 }}>
                      {href ? (
                        href.startsWith('/') ? (
                          <Link href={href === '/quote' && quoteHref ? quoteHref : href} style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
                            {content}
                          </Link>
                        ) : href.startsWith('tel:') ? (
                          <TrackedLink
                            href={href}
                            location="footer"
                            label="footer_contact_call"
                            style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}
                          >
                            {content}
                          </TrackedLink>
                        ) : (
                          <a
                            href={href}
                            target={href.startsWith('http') ? '_blank' : undefined}
                            rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                            aria-label={isInstagram ? 'Clear Guard on Instagram' : undefined}
                            style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', display: isInstagram ? 'inline-flex' : undefined }}
                          >
                            {content}
                          </a>
                        )
                      ) : (
                        <span>{label}</span>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Pre-footer CTA strip */}
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            padding: '32px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div className="display" style={{ fontSize: 22, color: '#fff', fontWeight: 700 }}>
              {productQuoteHref ? 'Window film for your home or business' : quoteHref ? 'Request a commercial installation quote' : 'Ready to protect your property?'}
            </div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', margin: '4px 0 0' }}>
              {productQuoteHref ? 'Solar and privacy film for homes, condos and businesses across Toronto & the GTA.' : quoteHref ? 'Solar, security and privacy film. GTA-based, serving commercial projects across Southern Ontario.' : 'Free security assessments across Toronto & the GTA.'}
            </p>
          </div>
          {quoteHref ? (
            <Link href={quoteHref} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {productQuoteHref ? 'Request a Film Quote' : 'Request Commercial Quote'} <IconArrowRight width={14} height={14} />
            </Link>
          ) : <BookingTrigger ctaLocation="footer">
            <button className="btn btn-primary" style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Get a Free Security Assessment <IconArrowRight width={14} height={14} />
            </button>
          </BookingTrigger>}
        </div>

        <div
          className="footer-legal"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 24,
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <span>&copy; {new Date().getFullYear()} {BUSINESS.name} Inc.</span>
          <span>
            <Link href="/privacy#cookie-choices" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy choices</Link>
            {' '}&middot;{' '}
            <Link href="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</Link>
          </span>
        </div>
      </div>
    </footer>
  )
}
